import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { soundManager } from '../../utils/sounds';
import {
  screenShake,
  confettiExplosion,
  getLevel,
  getStreak,
  getStats,
} from '../../utils/gameJuice';
import { DailyMissions } from '../components/DailyMissions';
import { SeasonBanner } from '../components/SeasonBanner';
import { TournamentBanner } from '../components/TournamentBanner';
export const HomePage: React.FC = () => {
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);

  useEffect(() => {
    // Check if returning player
    const stats = getStats();
    if (stats.totalGames > 0) {
      setShowWelcomeBack(true);
      setTimeout(() => setShowWelcomeBack(false), 4000);
    }
  }, []);

  const handleLinkClick = (sound: 'powerup' | 'select' = 'select', e: React.MouseEvent) => {
    soundManager.play(sound);
    if (sound === 'powerup') {
      screenShake('light');
      // Spawn particles from click position
      for (let i = 0; i < 8; i++) {
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('arcade:particle', {
              detail: { x: e.clientX, y: e.clientY, color: i % 2 === 0 ? '#00ffff' : '#ff10f0' },
            }),
          );
        }, i * 30);
      }
    }
  };

  const handleTitleClick = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
      soundManager.play('achievement');
      confettiExplosion();
      screenShake('medium');
    } else {
      soundManager.play('coin');
      screenShake('light');
    }
  };

  const level = getLevel();
  const streak = getStreak();

  return (
    <section className="space-y-8 pt-8">
      {/* Welcome Back Banner */}
      {showWelcomeBack && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="arcade-panel pixel-corners rounded-2xl p-4 text-center"
        >
          <span className="neon-text font-display text-sm">WELCOME BACK, CHAMPION!</span>
          {streak > 0 && (
            <span className="ml-4 font-arcade text-xs text-arcade-pink">{streak} WIN STREAK</span>
          )}
        </motion.div>
      )}

      <motion.div
        className="arcade-panel pixel-corners relative overflow-hidden rounded-xl p-6 shadow-arcade sm:p-10"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        <h1
          onClick={handleTitleClick}
          className="neon-text mt-4 max-w-3xl cursor-pointer font-display text-3xl font-bold leading-tight drop-shadow-neon transition-transform hover:scale-[1.02] sm:text-4xl lg:text-5xl"
        >
          ANTE • BATTLE • DOMINATE
        </h1>
        <p className="mt-4 max-w-2xl font-arcade text-base text-arcade-cyan sm:text-lg">
          Outscore your opponent. Win the pot. Every shuffle is verifiable, every payout is instant.
        </p>

        {/* Player Level Badge */}
        {level > 1 && (
          <div className="absolute right-2 top-2 flex items-center gap-2 rounded-lg border-2 border-arcade-yellow bg-black/50 px-3 py-1 sm:right-4 sm:top-4">
            <span className="font-pixel text-xs text-arcade-yellow">LVL</span>
            <span className="neon-text font-display text-lg">{level}</span>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/matchmaking"
            onClick={(e) => handleLinkClick('powerup', e)}
            onMouseEnter={() => soundManager.play('click')}
            className="arcade-btn pixel-corners rounded-2xl px-6 py-3 font-display text-xs font-semibold uppercase tracking-wide text-white shadow-card transition-all duration-150 hover:shadow-card-hover sm:text-sm"
          >
            ▸ FIND MATCH
          </Link>
          <Link
            to="/practice"
            onClick={(e) => handleLinkClick('select', e)}
            onMouseEnter={() => soundManager.play('click')}
            className="pixel-corners rounded-2xl border border-arcade-cyan/60 bg-black/50 px-6 py-3 font-display text-xs font-semibold uppercase text-arcade-cyan shadow-card transition-all duration-150 hover:border-arcade-cyan hover:bg-arcade-cyan/10 sm:text-sm"
          >
            ▸ PRACTICE
          </Link>
          <Link
            to="/info"
            onClick={(e) => handleLinkClick('select', e)}
            onMouseEnter={() => soundManager.play('click')}
            className="pixel-corners rounded-2xl border border-arcade-green/60 bg-black/50 px-6 py-3 font-display text-xs font-semibold uppercase text-arcade-green shadow-card transition-all duration-150 hover:border-arcade-green hover:bg-arcade-green/10 sm:text-sm"
          >
            ▸ INFO
          </Link>
          <Link
            to="/history"
            onClick={(e) => handleLinkClick('select', e)}
            onMouseEnter={() => soundManager.play('click')}
            className="pixel-corners rounded-2xl border border-arcade-purple/60 bg-black/50 px-6 py-3 font-display text-xs font-semibold uppercase text-arcade-purple shadow-card transition-all duration-150 hover:border-arcade-purple hover:bg-arcade-purple/10 sm:text-sm"
          >
            ▸ HISTORY
          </Link>
        </div>

        {/* Subtle corner brackets - no animation */}
        <div className="pointer-events-none absolute left-2 top-2 h-6 w-6 border-l-2 border-t-2 border-arcade-cyan/40" />
        <div className="pointer-events-none absolute bottom-2 right-2 h-6 w-6 border-b-2 border-r-2 border-arcade-pink/40" />
      </motion.div>

      {/* Tournament Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="relative"
      >
        <TournamentBanner />
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-black/60 backdrop-blur-[2px]">
          <span className="font-display text-lg tracking-wider text-white/70">Coming soon...</span>
        </div>
      </motion.div>

      {/* Season & Daily Missions Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="relative grid gap-6 md:grid-cols-2"
      >
        <SeasonBanner />
        <div className="relative">
          <DailyMissions />
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-black/60 backdrop-blur-[2px]">
            <span className="font-display text-lg tracking-wider text-white/70">
              Coming soon...
            </span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="grid gap-6 md:grid-cols-3"
      >
        {features.map((feature) => (
          <article
            key={feature.title}
            className="arcade-panel pixel-corners group relative overflow-hidden rounded-xl p-6 text-base text-white/80 transition-all duration-150 hover:border-arcade-cyan/50"
          >
            <h3 className="neon-text-pink font-display text-base font-semibold sm:text-lg">
              {feature.title}
            </h3>
            <p className="mt-3 font-arcade leading-relaxed">{feature.body}</p>
          </article>
        ))}
      </motion.div>
    </section>
  );
};

const features = [
  {
    title: 'VERIFIED SHUFFLES',
    body: 'Every deck is generated from combined player and server seeds. Verify any match after it ends — no trust required.',
  },
  {
    title: 'SECURE STAKES',
    body: 'SOL goes into an on-chain escrow that neither player nor operator can touch. Winner takes 97%, settled instantly.',
  },
  {
    title: 'FAST & FIERCE',
    body: 'Quick 3-5 minute matches with combo scoring, Joker abilities, and sabotage cards. Every turn counts.',
  },
];
